import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export const LOCATIONS = ["Storage 1", "Storage 2", "Office"];

function LocSelect({ value, onChange, testid, any }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{any && <SelectItem value="ANY">Any location (oldest first)</SelectItem>}{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function LocationBreakdown({ p }) {
  const ls = p.locationStock || {};
  return <div className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid={`loc-${p.sku}`}>{LOCATIONS.map((l) => `${l.replace("Storage ", "S")}: ${ls[l] || 0}`).join(" · ")}{p.reservedQuantity ? ` · ${p.reservedQuantity} reserved` : ""}</div>;
}

export function BatchList({ p }) {
  if (!p.batches?.length) return <span className="text-xs text-muted-foreground" data-testid={`batches-${p.sku}`}>No stock</span>;
  const rows = p.batches.reduce((acc, b) => {
    const last = acc[acc.length - 1];
    if (last && last.purchasePrice === b.purchasePrice && last.location === b.location) last.quantity += b.quantity;
    else acc.push({ ...b });
    return acc;
  }, []);
  return (
    <div className="space-y-0.5" data-testid={`batches-${p.sku}`}>
      {rows.map((b, i) => (
        <div key={i} className="text-xs whitespace-nowrap num" title={`${b.location} · inward ${new Date(b.inwardDate).toLocaleDateString("en-IN")}`}>
          <b>{b.quantity}x</b> @ ₹{Number(b.purchasePrice || 0).toLocaleString("en-IN")} <span className="text-muted-foreground">· {b.location.replace("Storage ", "S")}</span></div>))}
    </div>
  );
}

export function AdjustDialog({ product, onClose, onSaved }) {
  const [mode, setModeRaw] = useState("INWARD");
  const [f, setF] = useState({});
  const setMode = (m) => { setModeRaw(m); setF((x) => ({ ...x, location: "Storage 1" })); };
  useEffect(() => { if (product) { setMode("INWARD"); setF({ qty: 1, cost: product.fifoCost || product.purchasePrice || "", location: "Storage 1", from: "Storage 1", to: "Storage 2", note: "" }); } }, [product]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    const qty = +f.qty;
    if (!(qty > 0)) return toast.error("Enter a quantity");
    const body = mode === "TRANSFER" ? { mode, change: qty, fromLocation: f.from, toLocation: f.to, reason: f.note }
      : { mode, change: mode === "INWARD" ? qty : -qty, unitCost: mode === "INWARD" ? +f.cost : null, location: f.location === "ANY" ? null : f.location, reason: f.note || (mode === "INWARD" ? "Inward" : "Manual Correction") };
    try { const r = await api.post(`/inventory/${product.id}/adjust`, body); onSaved(product.id, 0, r.data); toast.success(mode === "TRANSFER" ? "Stock transferred" : "Stock updated"); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent data-testid="adjust-stock-dialog">
        <DialogHeader><DialogTitle>Adjust stock: {product?.name}</DialogTitle>
          <DialogDescription>Inward creates a new FIFO batch. Transfers move units between locations without changing cost.</DialogDescription></DialogHeader>
        <Tabs value={mode} onValueChange={setMode}><TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="INWARD" data-testid="adjust-mode-inward">Inward</TabsTrigger>
          <TabsTrigger value="TRANSFER" data-testid="adjust-mode-transfer">Storage Transfer</TabsTrigger></TabsList></Tabs>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" min={1} value={f.qty ?? ""} onChange={set("qty")} data-testid="adjust-qty-input" /></div>
          {mode === "INWARD" && <div className="space-y-1.5"><Label>Purchase price / unit (₹)</Label><Input type="number" value={f.cost ?? ""} onChange={set("cost")} data-testid="adjust-cost-input" /></div>}
          {mode === "INWARD" && <div className="space-y-1.5"><Label>Target location</Label><LocSelect value={f.location} onChange={set("location")} testid="adjust-location-select" /></div>}
          {mode === "TRANSFER" && <>
            <div className="space-y-1.5"><Label>Source location</Label><LocSelect value={f.from} onChange={set("from")} testid="adjust-from-select" /></div>
            <div className="space-y-1.5"><Label>Destination location</Label><LocSelect value={f.to} onChange={set("to")} testid="adjust-to-select" /></div></>}
          <div className="space-y-1.5 col-span-2"><Label>{mode === "INWARD" ? "Vendor / invoice note" : mode === "TRANSFER" ? "Reason / transfer note" : "Reason"}</Label>
            <Input value={f.note ?? ""} onChange={set("note")} data-testid="adjust-reason-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="adjust-save-button">{mode === "TRANSFER" ? "Transfer stock" : "Update stock"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
